import React from 'react';
import { Key, X, Sparkles, Plus, Trash2 } from 'lucide-react';

export interface KeyExtractionJob {
  id: string;
  snapshot: string;
  status: string;
  results?: any[];
  error?: string;
}

export interface KeyExtractionConfirmModalProps {
  show: boolean;
  keyExtractionSnapshot: string | null;
  keyExtractionSelection: { startX: number; endX: number; startY: number; endY: number } | null;
  keyExtractionJobs: KeyExtractionJob[];
  onReselect: () => void;
  onAddAndScanNext: () => void;
  onExtractSingle: () => void;
  onDeleteJob: (jobId: string) => void;
}

export const KeyExtractionConfirmModal: React.FC<KeyExtractionConfirmModalProps> = ({
  show,
  keyExtractionSnapshot,
  keyExtractionSelection,
  keyExtractionJobs,
  onReselect,
  onAddAndScanNext,
  onExtractSingle,
  onDeleteJob,
}) => {
  if (!show || !keyExtractionSnapshot) return null;

  return (
    <div
      className="key-extraction-confirm-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100002,
        backdropFilter: 'blur(4px)'
      }}
      onClick={onReselect}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '24px',
          width: '90%',
          maxWidth: '600px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h3 style={{
            margin: 0,
            color: 'var(--text-primary)',
            fontSize: '18px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <Key size={20} style={{ color: '#3b82f6' }} />
            Confirm Key Selection
          </h3>
          <button
            onClick={onReselect}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '4px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Preview Image */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{
            fontSize: '13px',
            color: 'var(--text-muted)',
            marginBottom: '10px',
            fontWeight: 500
          }}>
            Selected Area:
          </p>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '12px',
            border: '2px solid #3b82f6',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <img
              src={keyExtractionSnapshot}
              alt="Selected key area"
              style={{
                maxWidth: '100%',
                maxHeight: '300px',
                borderRadius: '8px',
                objectFit: 'contain'
              }}
            />
          </div>
          {keyExtractionSelection && (
            <p style={{
              margin: '10px 0 0 0',
              color: 'var(--text-muted)',
              fontSize: '12px',
              textAlign: 'center'
            }}>
              Size: {Math.abs(keyExtractionSelection.endX - keyExtractionSelection.startX).toFixed(0)} × {Math.abs(keyExtractionSelection.endY - keyExtractionSelection.startY).toFixed(0)}px
            </p>
          )}
        </div>

        {/* Queued areas with thumbnails */}
        {keyExtractionJobs.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8, fontWeight: 500 }}>
              Queued ({keyExtractionJobs.length})
            </label>
            <div style={{
              maxHeight: 140,
              overflowY: 'auto',
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              background: 'var(--bg-secondary)',
            }}>
              {keyExtractionJobs.map((job, idx) => (
                <div
                  key={job.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    borderBottom: idx < keyExtractionJobs.length - 1 ? '1px solid var(--border-color)' : 'none',
                  }}
                >
                  <img
                    src={job.snapshot}
                    alt={`Area ${idx + 1}`}
                    style={{
                      width: 56,
                      height: 38,
                      objectFit: 'cover',
                      borderRadius: 4,
                      border: '1px solid var(--border-strong)',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
                    Area {idx + 1}
                  </span>
                  <button
                    onClick={() => onDeleteJob(job.id)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--border-strong)',
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: 4,
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-faint)'; }}
                    title="Remove this area"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info Text */}
        <p style={{
          fontSize: '13px',
          color: 'var(--text-muted)',
          marginBottom: '20px',
          lineHeight: '1.5',
          textAlign: 'center'
        }}>
          This area will be analyzed to extract electrical symbols and their names.
        </p>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={onReselect}
            style={{
              padding: '12px 20px',
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-strong)',
              borderRadius: '8px',
              color: 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Reselect
          </button>
          <button
            onClick={onAddAndScanNext}
            style={{
              padding: '12px 20px',
              background: 'var(--bg-primary)',
              border: '2px solid #22c55e',
              borderRadius: '8px',
              color: 'var(--green-text)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f0fdf4'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-primary)'; }}
          >
            <Plus size={16} />
            Add & Scan Next
          </button>
          <button
            onClick={onExtractSingle}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            <Sparkles size={16} />
            {keyExtractionJobs.length > 0 ? 'Extract & Done' : 'Extract Power Legend'}
          </button>
        </div>
      </div>
    </div>
  );
};
